<template>
  <v-sheet
    data-testid="organizationSettings-sites"
    elevation="2"
    class="px-12 py-8 rounded white"
  >
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex">
        <p class="h6 pa-0 mb-0 me-3">
          {{ $t('common.team_other') }}
        </p>
        <CreateButton small @click="showCreateModal = true" />
      </div>
      <div>
        <SearchInput :value="searchTerms" @change="handleSearchTermsChange" />
      </div>
    </div>
    <TeamsList
      v-if="teams.length"
      class="mt-4"
      :teams="teams"
      :organizationId="organizationId"
      :highlightTerm="searchTerms"
      @change="fetchTeams"
      @edit="editTeam"
    />

    <TeamsEmptyState
      v-else
      :message="emptyStateMessage"
      @create="showCreateModal = true"
    />
    <v-row v-if="totalPages > 1" justify="center">
      <v-col cols="8">
        <v-pagination
          circle
          class="my-4 elevation-0"
          total-visible="5"
          :length="totalPages"
          :value="currentPage"
          @input="onPageChange"
        />
      </v-col>
    </v-row>
    <TeamFormDialog
      v-if="showCreateModal"
      :show="showCreateModal"
      :team="selectedTeam"
      :organizationId="organizationId"
      @close="closeTeamDialog"
      @updated="onUpdateTeam"
      @created="onUpdateTeam"
    />
  </v-sheet>
</template>

<script>
import { mapActions } from 'vuex'
import mixin from '@/mixins/mixin.vue'

import api from '@/api'
import TeamsList from '@/components/Teams/TeamsList.vue'
import TeamFormDialog from '@/components/TeamFormDialog.vue'
import CreateButton from '@/components/UI/CreateButton.vue'
import SearchInput from '@/components/Core/SearchInput.vue'

import TeamsEmptyState from '@/containers/OrganizationSettingsView/Teams/TeamsEmptyState.vue'

const ITEMS_PER_PAGE = 10

export default {
  name: 'Teams',

  components: {
    TeamsList,
    TeamFormDialog,
    CreateButton,
    SearchInput,
    TeamsEmptyState,
  },

  mixins: [mixin],

  props: {
    organizationId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      teams: [],
      currentPage: 1,
      totalPages: 0,
      showCreateModal: false,
      selectedTeam: {},
      searchTerms: '',
    }
  },

  async mounted() {
    this.fetchTeams()
  },

  computed: {
    emptyStateMessage() {
      if (this.searchTerms) return this.$t('teams.search_no_results_message')
      return this.$t('organization_settings.there_are_no_teams_added_yet')
    },
  },

  methods: {
    ...mapActions({
      createTeam: 'teams/createTeam',
      delete: 'teams/deleteTeam',
    }),

    handleSearchTermsChange(terms) {
      this.searchTerms = terms ?? ''
      this.currentPage = 1
      this.fetchTeams()
    },

    onPageChange(page) {
      if (this.currentPage === page) return

      this.currentPage = page
      this.fetchTeams()
    },

    async fetchTeams() {
      try {
        this.isLoading = true

        const options = {
          limit: ITEMS_PER_PAGE,
          from: (this.currentPage - 1) * ITEMS_PER_PAGE,
          sort: { by: '_score', order: 'desc' },
        }

        if (this.searchTerms) options.terms = this.searchTerms

        const response = await api.teams.getTeamsStream(
          this.organizationId,
          options
        )

        this.teams = response.data?.results.map(result => result.team) ?? []

        this.totalPages =
          Math.ceil(response?.data?.totalRecordsInData / ITEMS_PER_PAGE) || 0
      } catch (err) {
        this.teams = []
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    editTeam(team) {
      this.selectedTeam = team
      this.showCreateModal = true
    },

    closeTeamDialog() {
      this.showCreateModal = false
      this.selectedTeam = {}
    },

    onUpdateTeam() {
      this.fetchTeams()
      this.closeTeamDialog()
    },
  },
}
</script>
